import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Select, { components, ControlProps, SingleValue } from 'react-select';
import { useEffect, useState } from 'react';
import { Box, styled, Tab, Tabs } from '@mui/material';
import { useQuery } from 'react-query';

import { TrapTableType } from 'components/TrapsTables/trapTables';
import { SelectTrapDropdown } from 'api/traps/types';
import MultiCatchMouseTrapIcon from 'components/Icons/MultiCatchMouseTrapIcon';
import SmartBaitStationIcon from 'components/Icons/SmartRodentBaitModuleIcon';
import HubIcon from 'components/Icons/HubIcon';
import './DeviceDetailBreadCrump.scss';
import SnapTrapMouseIcon from 'components/Icons/SnapTrapMouseIcon';
import SnapTrapRatIcon from 'components/Icons/SnapTrapRatIcon';
import useGetDevicesInWarehouse from 'hooks/Traps/DeviceWarehouse/useGetDevicesWarehouse';
import DeviceWarehouseRespons from 'api/traps/DevicesWareHouse/types';
import useScreenSize from 'hooks/Screen/useScreenSize';
import objectNavigationData, { ObjectNavigationData } from 'pages/Object/ObjectNavigationData';
import { getObjectHeaderData } from 'api/objectHeader/getObjectHeaderData';
import DevicesStatusChart from 'components/Charts/DevicesStatusChart';

interface DeviceBreadCrumpProps {
  dropDownData: SelectTrapDropdown[] | null;
  objSlug: string;
  objName: string;
  sq: string | number;
  sn: string | null;
  deviceType: string;
  isLoading: boolean;
  slug: string;
  deviceName: string;
}
interface SearchableItem {
  serial_number: string;
  object_slug?: string;
}
const CustomTabs = styled(Tabs)({
  height: '100%',
  '& .MuiTabs-indicator': {
    display: 'none', // Disable the bottom line
  },
  '& .MuiTabs-scroller': { verticalAlign: 'middle' },

  '& .MuiTab-root': {
    minHeight: 0,
    minWidth: 'auto',

    textTransform: 'none',
    marginRight: '10px',
    color: '#6a37d4',
    fontSize: '16px',
    border: '2px solid #6a37d4',
    borderRadius: '5px',

    '&.Mui-selected': {
      color: '#6a37d4',
      borderRadius: '1rem',
      backgroundColor: 'rgba(111, 0, 133, 0.1)',
    },
  },
  '& .MuiTabs-scrollButtons.Mui-disabled': {
    display: 'none',
  },
});

const DeviceBreadCrump: React.FC<DeviceBreadCrumpProps> = ({
  slug,
  dropDownData,
  objSlug,
  objName,
  sn,
  deviceType,
  deviceName,
}) => {
  const { isNotTablet } = useScreenSize();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const iconMap: Record<string, JSX.Element> = {
    'Multi-catch mouse traps': <MultiCatchMouseTrapIcon height={30} width={30} />,
    'Smart rodent bait modules': <SmartBaitStationIcon height={30} width={30} />,
    'Snap traps (mouse)': <SnapTrapMouseIcon height={30} width={30} />,
    'Snap traps (rat)': <SnapTrapRatIcon height={30} width={30} />,
    Hubs: <HubIcon height={30} width={30} />,
  };
  const { data: headerData } = useQuery(['headerData', objSlug], () => getObjectHeaderData(objSlug || ''), {
    enabled: !!objSlug,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const [value, setValue] = useState<string | boolean>(false);
  const handleChange = (
    event: React.SyntheticEvent,
    newValue: string,
    toDjango: boolean = false,
    toPlan: boolean = false,
  ) => {
    if (toDjango && !toPlan) {
      window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}${newValue}/${objSlug}`;
    } else if (toPlan) {
      if (headerData?.header_plan) {
        window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}${newValue}/${objSlug}/${headerData.header_plan}`;
      } else if (headerData?.header_plan === null) {
        window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}/floor-plan/add/${objSlug}`;
      } else {
        console.error('headerData or header_plan is undefined');
      }
    } else {
      setValue(newValue);
      navigate(`${newValue}/${objSlug}`);
    }
  };
  const { data } = useGetDevicesInWarehouse(true);
  type DeviceUrlMap = Record<string, string | undefined>;
  const [deviceUrls, setDeviceUrls] = useState<DeviceUrlMap>({});
  useEffect(() => {
    if (sn && data) {
      const deviceKey = whatDeviceTake(deviceType as TrapTableType);

      if (deviceKey) {
        const targetArray = data[deviceKey as keyof DeviceWarehouseRespons];

        const list = targetArray as SearchableItem[] | undefined;

        const takeObjectSlug = list?.find(item => item.serial_number === sn)?.object_slug;
        const hubsLink = data.hub?.find(items => items.object_slug === takeObjectSlug)?.is_used?.detail_page_url_ending;
        const mctLink = data.mct?.find(items => items.object_slug === takeObjectSlug)?.is_used?.detail_page_url_ending;
        const srbmLink = data.srbm?.find(items => items.object_slug === takeObjectSlug)?.is_used
          ?.detail_page_url_ending;
        const smstLink = data.smst?.find(items => items.object_slug === takeObjectSlug)?.is_used
          ?.detail_page_url_ending;
        const srstLink = data.srst?.find(items => items.object_slug === takeObjectSlug)?.is_used
          ?.detail_page_url_ending;

        const newUrls: DeviceUrlMap = {};

        if (hubsLink) newUrls[TrapTableType.Hubs] = hubsLink;
        if (mctLink) newUrls[TrapTableType.MultiCatchMouseTraps] = mctLink;
        if (srbmLink) newUrls['Smart rodent bait modules'] = srbmLink;
        if (smstLink) newUrls['Snap traps (mouse)'] = smstLink;
        if (srstLink) newUrls['Snap traps (rat)'] = srstLink;

        setDeviceUrls(newUrls);
      }
    }
  }, [data, sn, deviceType]);
  const fullNames: Record<string, string> = {
    'Multi-catch mouse traps': 'Multi-catch mouse trap',
    'Smart rodent bait modules': 'Smart rodent bait module',
    'Snap traps (mouse)': 'Snap trap (mouse)',
    'Snap traps (rat)': 'Snap trap (rat)',
    Hubs: 'Hub',
  };
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
      minHeight: 'auto',
      cursor: 'pointer',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: 'white',
      border: '1px solid #6A37D4',
      zIndex: 9999,
      width: 'max-content',
      minWidth: '100%',
    }),
    option: (provided: any, state: any) => {
      const hasNbsp = state.label?.includes('\xa0');
      return {
        ...provided,
        backgroundColor: state.isSelected ? '#5f00cc' : state.isFocused ? '#d4b0f5' : 'white',
        color: hasNbsp ? '#f06548' : state.isSelected ? 'white' : 'black',
        cursor: 'pointer',
        padding: '8px 12px',
      };
    },
  };
  const handleSelectChange = (selectedOption: any) => {
    const selectedSlug = selectedOption.value;
    if (deviceName === 'hub') {
      navigate(`/hub/info/${selectedSlug}/`);
    } else {
      navigate(`/${selectedOption.url}`);
    }
  };
  const CustomDropdownIndicator = () => null;
  const CustomControl = ({ children, innerRef, innerProps, ...props }: ControlProps<any>) => (
    <components.Control {...props} innerRef={innerRef} innerProps={innerProps}>
      <div className="d-flex align-items-center cursor-pointer">
        <h4
          className="m-0 d-flex align-items-center fw-bold text-uppercase"
          style={{
            color: '#333',
            fontSize: '18px',
            letterSpacing: '0.5px',
          }}
        >
          {props.selectProps.placeholder}

          <div
            className="mx-2"
            style={{
              width: '1px',
              height: '20px',
              backgroundColor: '#ccc',
              display: 'inline-block',
            }}
          />

          <i className="ri-arrow-down-s-line" style={{ color: '#333' }} />
        </h4>

        <div style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}>{children}</div>
      </div>
    </components.Control>
  );
  const CustomOption = (props: any) => {
    const isHighlighted = props.isSelected || props.isFocused;

    return (
      <components.Option {...props}>
        <div className="d-flex align-items-center">
          <span className={`me-2 ${isHighlighted ? 'custom-color-for-icon' : ''}`}>{iconMap[props.data.value]}</span>
          <span>{props.data.label}</span>
        </div>
      </components.Option>
    );
  };
  const options = dropDownData?.map(trap => ({
    label: `${trap.is_activity_in ? '\xa0' : ''} № ${trap.sequence_number} ${trap.is_activity_in ? '\xa0' : ''}`,
    value: trap.slug,
    url: trap.detail_page_url_ending,
    isActivity: trap.is_activity_in,
    sn: trap.sequence_number,
  }));
  const optionsDevice = Object.entries(deviceUrls)
    .filter(([_, url]) => url !== undefined)
    .map(([type, url]) => ({
      label: fullNames[type] || type,
      value: type,
      url: url,
    }));
  const activeOption = options?.find(
    option => String(option.value) === String(slug) || String(option.sn) === String(sn),
  );
  const renderDeviceIconAndLabel = (deviceType: TrapTableType) => {
    return (
      <div className="d-flex align-items-center">
        <div className="me-2">{iconMap[deviceType] || <MultiCatchMouseTrapIcon height={30} width={30} />}</div>

        <h4 className="m-0 mx-2 d-flex align-items-center gap-2">
          <Select
            className="badge p-0"
            value={optionsDevice?.find(option => option.value === deviceType)}
            options={optionsDevice}
            onChange={(opt: any) => navigate(`/${opt.url}`)}
            placeholder={fullNames[deviceType] || deviceType}
            styles={customStyles}
            isSearchable={false}
            components={{
              Control: CustomControl,
              DropdownIndicator: CustomDropdownIndicator,
              IndicatorSeparator: () => null,
              Option: CustomOption,
            }}
          />
        </h4>
      </div>
    );
  };
  const renderNaming = (deviceType: TrapTableType) => {
    switch (deviceType) {
      case TrapTableType.MultiCatchMouseTraps:
        return <span>MCT</span>;
      case TrapTableType.SmartRodenBaitModule:
        return <span>SRBM</span>;
      case TrapTableType.Hubs:
        return <span>Hub</span>;
      case TrapTableType.SnapTrapMouse:
        return <span>SMST</span>;
      case TrapTableType.SnapTrapRat:
        return <span>SRST</span>;
      default:
        return null;
    }
  };
  const whatDeviceTake = (deviceType: TrapTableType) => {
    if (deviceType === TrapTableType.MultiCatchMouseTraps) {
      return 'mct';
    } else if (deviceType === TrapTableType.SmartRodenBaitModule) {
      return 'srbm';
    } else if (deviceType === TrapTableType.Hubs) {
      return 'hub';
    } else if (deviceType === TrapTableType.SnapTrapMouse) {
      return 'smst';
    } else if (deviceType === TrapTableType.SnapTrapRat) {
      return 'srst';
    }
  };
  const handleDropdownChange = (newValue: SingleValue<ObjectNavigationData>) => {
    if (!newValue) return;

    const selectedValue = newValue.path;
    const selectedNavItem = objectNavigationData.find(item => item.path === selectedValue);

    if (selectedNavItem?.toDjango && !selectedNavItem?.toPlan) {
      window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}${selectedValue}/${slug}`;
    } else if (selectedNavItem?.toPlan) {
      if (headerData?.header_plan) {
        window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}${selectedValue}/${slug}/${headerData.header_plan}`;
      } else {
        window.location.href = `${process.env.REACT_APP_BASE_DJANGO_URL}floor-plan/add/${slug}`;
      }
    } else {
      setValue(selectedValue);
      navigate(`${selectedValue}/${slug}`);
    }
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="page-title-box d-lg-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center flex-wrap justify-content-between">
            {renderDeviceIconAndLabel(deviceType as TrapTableType)}
            <Select
              className="badge fs-18 md:fs-20 p-0 mx-3"
              styles={{
                control: provided => ({
                  ...provided,
                  minWidth: '90px',
                  backgroundColor: '#6A37D4',
                  color: 'white',
                  border: 'none',
                  padding: '1px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: 'none',
                  fontWeight: 'bold',
                }),
                singleValue: provided => ({
                  ...provided,
                  color: 'white',
                }),
                placeholder: provided => ({
                  ...provided,
                  color: 'white',
                }),
                menu: provided => ({
                  ...provided,
                  backgroundColor: 'white',
                  outline: 'none',
                  border: '1px solid #6A37D4',
                  boxShadow: 'none',
                  zIndex: 9999,
                }),
                option: (provided, state) => {
                  const option = options?.find(opt => opt.value === state.data.value);
                  const hasNbsp = option?.label?.includes('\xa0') || option?.value?.includes('\xa0');

                  return {
                    ...provided,
                    backgroundColor: state.isSelected ? '#5f00cc' : state.isFocused ? '#d4b0f5' : 'white',
                    color: hasNbsp ? '#f06548' : state.isSelected ? 'white' : state.isFocused ? 'white' : 'black',
                    cursor: 'pointer',
                    fontSize: '14px',
                  };
                },
                dropdownIndicator: provided => ({
                  ...provided,
                  color: 'white',
                }),
              }}
              options={options}
              onChange={handleSelectChange}
              value={activeOption}
              isSearchable
              getOptionLabel={e => e.label}
            />
          </div>

          <div className="mt-3 mt-lg-0">
            <ol className="breadcrumb m-0 d-flex justify-content-center  align-items-center">
              <li className="breadcrumb-item">
                <a href="/r/client-dashboard">
                  <span className="fw-bold">{t('MCTDetail.DeviceBreadCrump.myClients')}</span>
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href={`/r/object/traps/${objSlug}`}>
                  <span className="fw-bold">
                    {t('MCTDetail.DeviceBreadCrump.smartDevicesIn')} "{objName}"
                  </span>
                </a>
              </li>
              <li className="breadcrumb-item text-muted">
                {renderNaming(deviceType as TrapTableType)} № {sn}
              </li>
            </ol>
          </div>
        </div>
        <div className="container-fluid" style={{ padding: 0 }}>
          <div className="page-title-box  d-flex align-items-center justify-content-between">
            <Box sx={{ width: '100%', height: '100%' }} className="mb-0 mb-sm-0">
              {isNotTablet ? (
                <div className="d-flex flex-column align-items-center justify-content-between">
                  <Select
                    styles={{
                      control: provided => ({
                        ...provided,
                        backgroundColor: 'white',
                        color: 'black',
                        border: '2px solid #6a37d4',
                        padding: '0px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        boxShadow: 'none',
                        fontWeight: 'bold',
                        width: '200px',
                        minWidth: '180px',
                        margin: '0 auto',
                      }),
                      singleValue: provided => ({
                        ...provided,
                        color: 'black',
                      }),
                      placeholder: provided => ({
                        ...provided,
                        color: 'black',
                      }),
                      menu: provided => ({
                        ...provided,
                        backgroundColor: 'white',
                        zIndex: 9999,
                        outline: 'none',
                        border: '1px solid #6a37d4',
                        boxShadow: 'none',
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#5f00cc' : state.isFocused ? '#d4b0f5' : 'white',
                        color: state.isSelected ? 'white' : state.isFocused ? 'white' : 'black',
                        cursor: 'pointer',
                      }),
                      dropdownIndicator: provided => ({
                        ...provided,
                        color: 'black',
                      }),
                    }}
                    options={objectNavigationData}
                    placeholder="Go to.."
                    isSearchable
                    value={objectNavigationData.find(option => option.path === value)}
                    onChange={handleDropdownChange}
                    getOptionLabel={e => t(e.title as any)}
                    getOptionValue={e => e.path}
                  />
                  <div className="d-lg-none d-flex flex-column mt-3 mt-xl-0 align-items-start mx-xl-2">
                    <div className="d-flex align-items-center justify-content-center gap-2" style={{ width: '200px' }}>
                      {headerData?.header_fast_data && headerData.header_fast_data.mct_count > 0 && (
                        <span className="badge rounded-3 bg-success">
                          Mice: {headerData.header_fast_data.mct_mice_caught}
                        </span>
                      )}
                      {headerData?.header_fast_data && headerData.header_fast_data.srbm_count > 0 && (
                        <span className="badge rounded-3 bg-success">
                          Bait consumed: {headerData.header_fast_data.bait_eaten}g
                        </span>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {headerData?.header_fast_data && (
                        <DevicesStatusChart
                          mctCount={headerData.header_fast_data.mct_count}
                          mctActive={headerData.header_fast_data.mct_active}
                          sbsCount={headerData.header_fast_data.srbm_count}
                          sbsActive={headerData.header_fast_data.srbm_active}
                          srstCount={headerData.header_fast_data.srst_count}
                          srstActive={headerData.header_fast_data.srst_active}
                          smstCount={headerData.header_fast_data.smst_count}
                          smstActive={headerData.header_fast_data.smst_active}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <CustomTabs
                  value={value}
                  onChange={(event, newValue) => {
                    const selectedNavItem = objectNavigationData.find(item => item.path === newValue);
                    handleChange(event, newValue, selectedNavItem?.toDjango, selectedNavItem?.toPlan);
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="nav tabs example"
                  allowScrollButtonsMobile
                  role="navigation"
                  sx={{ bgcolor: 'background.paper', maxWidth: 'fit-content', minHeight: '100%' }}
                  selectionFollowsFocus
                >
                  {objectNavigationData.map(item => (
                    <Tab
                      sx={{ height: '38px' }}
                      key={item.title}
                      label={
                        <div className="d-flex align-items-center">
                          <i className={`${item.icon} header-icon-style`} />
                          <div
                            className="d-flex align-items-center fs-14 bold"
                            style={{ fontFamily: 'Poppins, sans-serif' }}
                          >
                            {t(item.title as any)}
                          </div>
                        </div>
                      }
                      value={item.path}
                    />
                  ))}
                </CustomTabs>
              )}
            </Box>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceBreadCrump;
